import * as React from 'react';
import * as AWS from 'aws-sdk';
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';

export interface FolderDownloadButtonProps {
  folderKey: string;
  zipName: string;
  bucketName: string;
  region?: string;
  onError?: (message: string) => void;
  onComplete?: () => void;
}

export function FolderDownloadButton({
  folderKey,
  zipName,
  bucketName,
  region,
  onError,
  onComplete,
}: FolderDownloadButtonProps) {
  const [zipProgress, setZipProgress] = React.useState({ completed: 0, total: 0 });
  const [status, setStatus] = React.useState<'idle' | 'running' | 'error' | 'done'>('idle');
  const [completed, setCompleted] = React.useState(false);

  const handleClick = React.useCallback(async () => {
    setCompleted(false);
    setStatus('running');

    try {
      if (region) AWS.config.update({ region });
      const s3 = new AWS.S3({ params: { Bucket: bucketName } });

      const Prefix = (folderKey || '').replace(/\/+$/, '') + '/';

      const allKeys: string[] = [];
      let token: string | undefined;
      do {
        const resp = await s3.listObjectsV2({ Bucket: bucketName, Prefix, ContinuationToken: token }).promise();

        for (const o of resp.Contents ?? []) {
          const k = o.Key!;
          if (k !== Prefix && !k.endsWith('/')) allKeys.push(k);
        }

        token = resp.IsTruncated ? resp.NextContinuationToken : undefined;
      } while (token);

      if (allKeys.length === 0) {
        setStatus('idle');
        onError?.('This folder is empty — nothing to download.');
        return;
      }

      setZipProgress({ completed: 0, total: allKeys.length });

      const CHUNK_SIZE = 200;
      const chunks: string[][] = [];
      for (let i = 0; i < allKeys.length; i += CHUNK_SIZE) {
        chunks.push(allKeys.slice(i, i + CHUNK_SIZE));
      }

      for (let partIndex = 0; partIndex < chunks.length; partIndex++) {
        const zip = new JSZip();
        const chunk = chunks[partIndex];

        const CONCURRENCY = 5;
        let i = 0;
        async function work() {
          while (i < chunk.length) {
            const idx = i++;
            const key = chunk[idx];

            const obj = await s3.getObject({ Bucket: bucketName, Key: key }).promise();

            const relativePath = key.slice(Prefix.length);
            zip.file(relativePath, obj.Body as any);

            setZipProgress(p => ({ completed: p.completed + 1, total: p.total }));
          }
        }
        await Promise.all(Array.from({ length: CONCURRENCY }, work));

        const out = await zip.generateAsync({ type: 'blob' });
        const name = chunks.length === 1 ? `${zipName}.zip` : `${zipName}-part${partIndex + 1}-of-${chunks.length}.zip`;
        saveAs(out, name);
      }

      setStatus('done');
      setCompleted(true);
      onComplete?.();
    } catch (e) {
      const err = e as any;
      setStatus('error');
      onError?.(`Download error: ${err?.message ?? String(err)}`);
    }
  }, [folderKey, zipName, bucketName, region, onError, onComplete]);

  return (
    <div style={{ display: 'inline-block' }}>
      <button
        onClick={handleClick}
        disabled={status === 'running'}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', verticalAlign: 'middle' }}
        title="Download folder as ZIP"
      >
        <svg width="2em" height="2em" viewBox="0 0 16 16" fill="#34495e" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"
          />
          <path
            fillRule="evenodd"
            d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"
          />
        </svg>
      </button>

      {status === 'running' && (
        <div style={{ width: 120, marginTop: 4 }}>
          <progress value={zipProgress.completed} max={zipProgress.total} style={{ width: '100%' }} />
          <small>
            {zipProgress.completed}/{zipProgress.total}
          </small>
        </div>
      )}

      {completed && (
        <div style={{ color: 'green', marginTop: 4 }}>
          <small>Download complete ✓</small>
        </div>
      )}
    </div>
  );
}
